import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import Search from './pages/Search';
// import Detail from './pages/Detail';
import NotFound from './pages/NotFound';

import { PageTracker, PageLifecycleTracker } from './utils/page-tracker';
import { PageTrackerDebugger } from './utils/page-tracker-debug';
import { CacheDebugger } from './utils/cache-debug';

PageTracker.init();

// 初始化页面生命周期追踪器，自动处理页面卸载事件
PageLifecycleTracker.init();

// 添加全局调试方法（仅开发环境）
if (process.env.NODE_ENV === 'development') {
  interface WindowWithDebug extends Window {
    PageTracker: typeof PageTracker;
    PageLifecycleTracker: typeof PageLifecycleTracker;
    PageTrackerDebugger: typeof PageTrackerDebugger;
    CacheDebugger: typeof CacheDebugger;
    debugPageTracker: () => Promise<void>;
    quickTest: () => Promise<void>;
    runCacheDiagnostic: () => void;
    testUnloadCache: () => void;
    forceCreateCache: () => void;
    testPageUnload: () => void;
  }
  
  const windowWithDebug = window as unknown as WindowWithDebug;
  windowWithDebug.PageTracker = PageTracker;
  windowWithDebug.PageLifecycleTracker = PageLifecycleTracker;
  windowWithDebug.PageTrackerDebugger = PageTrackerDebugger;
  windowWithDebug.CacheDebugger = CacheDebugger;
  windowWithDebug.debugPageTracker = async () => {
    await PageTrackerDebugger.runFullDiagnostic();
  };
  windowWithDebug.runCacheDiagnostic = () => {
    CacheDebugger.runCacheDiagnostic();
  };
  windowWithDebug.testUnloadCache = () => {
    CacheDebugger.testUnloadCache();
  };
  windowWithDebug.forceCreateCache = () => {
    CacheDebugger.forceCreateCache();
  };
  windowWithDebug.testPageUnload = () => {
    console.log('🧪 测试页面卸载缓存...');
    const beforeCount = PageTracker.getCachedDurationCount();
    console.log('卸载前缓存数量:', beforeCount);
    
    // 手动触发页面卸载事件
    PageLifecycleTracker.onPageUnload();
    
    const afterCount = PageTracker.getCachedDurationCount();
    console.log('卸载后缓存数量:', afterCount);
    
    if (afterCount > beforeCount) {
      console.log('✅ 页面卸载缓存测试成功');
      console.log('新增缓存数据:', localStorage.getItem('page_duration_cache'));
    } else {
      console.error('❌ 页面卸载缓存测试失败');
    }
  };
  windowWithDebug.quickTest = async () => {
    console.log('🔍 开始快速测试...');
    
    // 1. 初始化检查
    if (!PageTracker.isReady()) {
      console.log('正在初始化 PageTracker...');
      await PageTracker.init();
    }
    
    // 2. 生成测试数据
    console.log('生成测试数据...');
    PageTracker.cachePageDuration();
    PageTracker.cachePageDuration();
    
    // 3. 检查缓存
    const cacheCount = PageTracker.getCachedDurationCount();
    console.log('缓存数量:', cacheCount);
    
    // 4. 检查localStorage
    const rawCache = localStorage.getItem('page_duration_cache');
    console.log('localStorage 原始数据:', rawCache);
    
    // 5. 执行上报
    console.log('开始批量上报...');
    await PageTracker.flushCachedDurations();
    
    // 6. 检查上报后状态
    const afterCount = PageTracker.getCachedDurationCount();
    console.log('上报后缓存数量:', afterCount);
    
    console.log('✅ 测试完成');
  };
  
  console.log('🛠️ 调试工具已加载到全局作用域:');
  console.log('- window.PageTracker: PageTracker 类');
  console.log('- window.PageLifecycleTracker: 页面生命周期追踪器');
  console.log('- window.PageTrackerDebugger: 调试器类');
  console.log('- window.CacheDebugger: 缓存调试器类');
  console.log('- window.debugPageTracker(): 运行完整诊断');
  console.log('- window.quickTest(): 快速测试');
  console.log('- window.runCacheDiagnostic(): 缓存问题排查');
  console.log('- window.testUnloadCache(): 测试页面卸载缓存');
  console.log('- window.testPageUnload(): 测试页面卸载事件');
  console.log('- window.forceCreateCache(): 强制生成测试缓存');
}

// 自定义滚动行为组件
const ScrollBehavior: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  // 禁用路由变化时自动滚动到顶部的行为
  useEffect(() => {
    // 不执行任何滚动操作
  }, [location.pathname]);

  return <>{children}</>;
};

// // 重定向组件，用于从旧路由重定向到新路由
// const DetailRedirect: React.FC = () => {
//   const { id } = useParams<{ id: string }>();
//   return <Navigate to={`/detailsBackup?id=${id}&episode=1`} replace />;
// };

const App: React.FC = () => {
  return (
    <ScrollBehavior>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="search" element={<Search />} />
          {/* <Route path="detailsBackup" element={<Detail />} /> */}
          {/* <Route path="detail/:id" element={<DetailRedirect />} /> */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </ScrollBehavior>
  );
};

export default App; 